import { inject, Injectable } from '@angular/core';
import { IBaryTableFetchService, TableOptions } from './i-bary-table-fetch-service';
import { Transport } from '../models/transport.model';
import { HttpResponse } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { createRequestOption, mapAsBody } from '../app/shared/util/request-util';
import { ApiProviderService } from './api-provider.service';
import { API_ENDPOINTS } from '../models/enums/endpoints.enum';
import { AddToBasket } from '../models/add-to-basket.model';
import { NewQuotationsStats, QuotationStats, QuotationStatusEnum } from '../models/enums/quotation-status.enum';
import { QuotationView } from '../models/quotation-view.model';

@Injectable({ providedIn: 'root' })
export class QuotationService implements IBaryTableFetchService<Transport> {
  readonly #apiProvider = inject(ApiProviderService);

  protected quotationUrl = API_ENDPOINTS.QUOTATION;
  protected acceptQuotationUrl = API_ENDPOINTS.QUOTATION_ACCEPT;
  protected denyQuotationUrl = API_ENDPOINTS.QUOTATION_DENY;
  protected resetQuotationUrl = API_ENDPOINTS.QUOTATION_RESET;
  protected quotationStats = API_ENDPOINTS.QUOTATION_STATS;
  protected quotationsResponseTime = API_ENDPOINTS.QUOTATION_RESPONSE_TIME;

  public requestQuotation(transport: Transport): Observable<AddToBasket> {
    return this.#apiProvider.post<AddToBasket>(this.quotationUrl, transport.serialize()).pipe(mapAsBody);
  }

  public denyQuotation(transport: Transport): Observable<string> {
    return this.#apiProvider.put<string>(this.denyQuotationUrl(transport.id), transport.serialize()).pipe(mapAsBody);
  }

  public acceptQuotation(transport: Transport): Observable<string> {
    return this.#apiProvider.put<string>(this.acceptQuotationUrl(transport.id), transport.serialize()).pipe(mapAsBody);
  }

  public getQuotationsStats(): Observable<QuotationStats[]> {
    return this.#apiProvider.get<QuotationStats[]>(this.quotationStats).pipe(mapAsBody);
  }

  public getQuotationsResponseTime(): Observable<NewQuotationsStats> {
    return this.#apiProvider.get<NewQuotationsStats>(this.quotationsResponseTime).pipe(mapAsBody);
  }

  public resetQuotation(transport: Transport): Observable<string> {
    return this.#apiProvider.put<string>(this.resetQuotationUrl(transport.id), transport.serialize()).pipe(mapAsBody);
  }

  // customer
  public query(filters: any, serviceOptions: any): Observable<HttpResponse<QuotationView[]>> {
    filters = this.addCriteriaQuotationStatus(filters, serviceOptions);
    const options = createRequestOption(filters);
    return this.#apiProvider
      .get<QuotationView[]>(this.quotationUrl, {
        params: options,
        observe: 'response',
      })
      .pipe(
        map((res: HttpResponse<QuotationView[]>) => {
          return new HttpResponse<QuotationView[]>({
            body: (res.body || []).map(v => new QuotationView(v)),
            url: res.url as string,
            headers: res.headers,
            status: res.status,
          });
        })
      );
  }

  private addCriteriaQuotationStatus(filters: any, tableOptions?: TableOptions): any {
    // TODO check all cases are correctly filtered.
    if (tableOptions?.state) {
      if (tableOptions.state === 'NEW') {
        filters['quotationStatus.in'] = [QuotationStatusEnum.NEW];
      } else if (tableOptions.state === 'ONGOING') {
        filters['quotationStatus.in'] = [QuotationStatusEnum.TRANSPORTER_ACCEPTED];
      } else {
        // old
        filters['quotationStatus.in'] = [
          QuotationStatusEnum.CUSTOMER_ACCEPTED,
          QuotationStatusEnum.CUSTOMER_REJECTED,
          QuotationStatusEnum.TRANSPORTER_REJECTED,
          QuotationStatusEnum.TRANSPORTER_EXPIRED_PICKUP_DATE,
          QuotationStatusEnum.FINALIZED,
          QuotationStatusEnum.CUSTOMER_EXPIRED_PICKUP_DATE,
          QuotationStatusEnum.CUSTOMER_EXPIRED_VALIDITY_DATE,
          QuotationStatusEnum.CUSTOMER_ACCEPTED_BUT_EXPIRED_VALIDITY_DATE,
          QuotationStatusEnum.CUSTOMER_ACCEPTED_BUT_EXPIRED_PICKUP_DATE,
        ];
      }
    }
    return filters;
  }

  fillDataToShow(entities: Transport[]): Observable<Transport[]> {
    return of(entities);
  }
}
